import { useApi } from '@hooks/useApi';
import { Blockquote } from '@ui';
import { useModal } from '@hooks/useModal';
import useCheckPermissions from '~/hooks/useCheckPermissions';
import { ClubMember, ClubMemberProperty } from 'components/ClubMemberManagement/types';
import { Form } from 'components/Form/base/Form';
import { FormDivider, FormInput, FormSubmit } from 'components/Form/next';
import Loader from 'components/Loader';
import Joi from 'joi';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { addressSchemaBuilder } from 'validation/schema/addressSchema';
import { usePaymentTerms } from '../../../UserInvoice/data';
import GroupGuesser from '../../ClubMemberGroup/GroupGuesser';
import { ClubMemberFileManager } from '../../ClubMemberFiles';
import { renderFieldForProperty } from '../../helpers';
import { ClubMemberNumberField } from './ClubMemberNumberField';

interface Props {
    clubSlug: string;
    member?: ClubMember;
    callback?: (newClubMember: ClubMember) => void;
}

export const ClubMemberForm = ({ clubSlug, member, callback }: Props) => {
    const { t } = useTranslation('member-management');
    const api = useApi();
    const { hasAccess: canViewFiles } = useCheckPermissions({ clubSlug, permission: 'member_management.file' });
    const { data: paymentTermsData, isLoading: isLoadingPaymentTerms } = usePaymentTerms(clubSlug);
    const { data: propertyData, isLoading: isLoadingProperties } = useSWR<{ data: ClubMemberProperty[] }>(
        clubSlug ? `club-member/${clubSlug}/property` : null
    );
    const [isLoading, setIsLoading] = useState(false);
    const [setModal] = useModal();

    const handleSubmit = (data) => {
        setIsLoading(true);
        return api({
            url: `club-member/${member?.id || clubSlug}`,
            method: member ? 'PUT' : 'POST',
            data,
            onBefore: () => setIsLoading(false),
            onSuccess: ({ data }) =>
                !member
                    ? setModal({
                          title: t('clubMember.form.groupAssignModalTitle'),
                          content: <GroupGuesser memberId={data.id} callback={() => callback?.(data)} />,
                          show: true,
                          size: 50
                      })
                    : callback?.(data)
        });
    };

    const schema = Joi.object({
        firstname: Joi.string().required().label(t('clubMember.form.fields.firstname')),
        lastname: Joi.string().required().label(t('clubMember.form.fields.lastname')),
        email: Joi.string().required().label(t('clubMember.form.fields.emailAddress')),
        gender: Joi.string()
            .required()
            .valid('männlich', 'weiblich', 'divers')
            .label(t('clubMember.form.fields.gender')),
        remarks: Joi.string().allow(null, '').max(10000).label(t('clubMember.form.fields.remarks')),
        phone: Joi.string().allow(null, '').label(t('clubMember.form.fields.phone')),
        birthday: Joi.date().allow(null, '').less(new Date()).label(t('clubMember.form.fields.birthday')),
        entry_date: Joi.date().allow(null, '').label(t('clubMember.form.fields.entryDate')),
        exit_date: Joi.date().allow(null, '').label(t('clubMember.form.fields.exitDate')),
        ...addressSchemaBuilder((f) => f.allow(null, ''))
    }).unknown();

    const renderInputsFromProperties = useCallback(
        () =>
            propertyData?.data.map((property) => {
                const props = {
                    key: property.id,
                    name: 'p_' + property.id,
                    label: property.title + (property.required ? ' *' : '')
                };

                return renderFieldForProperty(property, props);
            }),
        [propertyData]
    );

    return (
        <Loader isLoading={isLoadingProperties || isLoadingPaymentTerms}>
            <Form
                onSubmit={handleSubmit}
                validationSchema={schema}
                setDefaultValuesOnRerender={false}
                defaultValues={{ entry_date: new Date(), send_application_accepted_notification: false, ...member }}
            >
                <div className="tw-grid tw-grid-cols-2 tw-gap-12">
                    <div>
                        <FormDivider small title={t('clubMember.form.sections.general')} />
                        <ClubMemberNumberField />
                        <FormInput
                            secure
                            helperText={t('clubMember.form.fields.debtorNumberHelper')}
                            name="debtor_number"
                            label={t('clubMember.form.fields.debtorNumber')}
                        />
                        <FormInput.Select
                            options={paymentTermsData?.data?.map((pt) => ({ key: pt.id, label: pt.title })) ?? []}
                            name="payment_term_id"
                            label={t('clubMember.form.fields.paymentTerms')}
                        />
                        <FormInput name="firstname" label={t('clubMember.form.fields.firstname')} />
                        <FormInput name="lastname" label={t('clubMember.form.fields.lastname')} />
                        <FormInput.Select
                            options={[
                                { key: 'männlich', label: t('clubMember.form.fields.genderOptions.male') },
                                {
                                    key: 'weiblich',
                                    label: t('clubMember.form.fields.genderOptions.female')
                                },
                                { key: 'divers', label: t('clubMember.form.fields.genderOptions.diverse') }
                            ]}
                            name="gender"
                            label={t('clubMember.form.fields.gender')}
                        />
                        <FormInput type="email" name="email" label={t('clubMember.form.fields.email')} />
                        <FormInput
                            name="pin"
                            label={t('clubMember.form.fields.pin')}
                            helperText={t('clubMember.form.fields.pinHelper')}
                        />
                        <FormInput type="tel" name="phone" label={t('clubMember.form.fields.phone')} />
                        <FormInput.DatePicker name="birthday" label={t('clubMember.form.fields.birthday')} />
                        <FormInput.DatePicker name="entry_date" label={t('clubMember.form.fields.entryDate')} />
                        <Blockquote className="tw-mb-4">{t('clubMember.form.fields.exitDateMoved')}</Blockquote>
                        <FormInput.Textarea name="remarks" label={t('clubMember.form.fields.remarks')} />
                        <FormInput.Address name="address" label={t('clubMember.form.fields.address')} />
                    </div>
                    <div>
                        <FormDivider small title={t('clubMember.form.sections.properties')} />
                        {propertyData?.data?.length > 0 && renderInputsFromProperties()}
                    </div>
                </div>
                {(!member?.id || canViewFiles) && (
                    <FormDivider small title={t('clubMember.form.fields.files')} />
                )}
                {member?.id ? (
                    canViewFiles && (
                        <div className="tw-my-4">
                            <ClubMemberFileManager memberId={member.id} />
                        </div>
                    )
                ) : (
                    <FormInput.Files name="files" valueMapping={(id) => ({ id })} className="tw-my-4" />
                )}
                <FormInput.Checkbox
                    name="send_application_accepted_notification"
                    label={t('clubMember.form.fields.notifyAcceptance')}
                />
                <FormSubmit isLoading={isLoading} />
            </Form>
        </Loader>
    );
};
